import * as React from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_FLAGS, LANGUAGE_NAMES, type SupportedLanguage } from "@tellescope/i18n";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { cn } from "../lib/utils";

export interface LanguageSelectorProps {
  locales?: SupportedLanguage[];
  size?: "sm" | "default";
  className?: string;
}

const LanguageSelector = React.forwardRef<HTMLButtonElement, LanguageSelectorProps>(
  ({ className, size = "default", locales = ["en", "es", "fr", "de", "zh"], ...props }, ref) => {
    const { i18n } = useTranslation();
    const language = i18n.language;

    const handleLanguageChange = async (newLanguage: string) => {
      await i18n.changeLanguage(newLanguage);
    };

    return (
      <Select value={language} onValueChange={handleLanguageChange}>
        <SelectTrigger
          ref={ref}
          size={size}
          className={cn("w-auto min-w-[130px] [&_span]:overflow-visible [&_span]:text-clip", className)}
          {...props}
        >
          <SelectValue>
            <span className="flex items-center gap-2">
              <span data-testid="language-selector-flag">{LANGUAGE_FLAGS[language as SupportedLanguage]}</span>
              <span data-testid="language-selector-name">{LANGUAGE_NAMES[language as SupportedLanguage]}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {locales.map((localeOption) => (
            <SelectItem key={localeOption} value={localeOption}>
              <span className="flex items-center gap-2">
                <span data-testid="language-selector-flag">{LANGUAGE_FLAGS[localeOption]}</span>
                <span data-testid="language-selector-name">{LANGUAGE_NAMES[localeOption]}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
);
LanguageSelector.displayName = "LanguageSelector";

export { LanguageSelector };
